import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';

import type { WeightTrajectories } from '@loggy/shared';

/** The five lines this chart can draw, by the name the legend gives them. */
type TrajectorySeriesKey = 'actual' | 'required' | 'healthyFromToday' | 'projected' | 'drift';

import { Text } from '../design/index.js';
import { buildTicks, isoWeekNumber, linearPath } from './chartMath.js';
import { ChartLegend } from './ChartLegend.js';
import type { ChartLegendItem } from './ChartLegend.js';
import {
  anchorTimeForIndex,
  deadlineLabelAnchor as anchorForDeadlineLabel,
  linearScale,
  mapTrajectoryPoints,
  tooltipVerticalAlign,
  trajectoryTimeDomain,
  trajectoryValueDomain,
  weekGridForDomain,
  weeklyMarkerIndices,
  weeklyReadoutEntries,
  WEEKLY_READOUT_TOLERANCE_MS,
} from './weightTrajectoryChartMath.js';
import type { NamedTrajectorySeries, TrajectoryPixel } from './weightTrajectoryChartMath.js';
import styles from './WeightTrajectoryChart.module.css';

export { linearScale, mapTrajectoryPoints, trajectoryTimeDomain, trajectoryValueDomain } from './weightTrajectoryChartMath.js';
export type { TrajectoryPixel } from './weightTrajectoryChartMath.js';

/**
 * A pure, presentational, hand-written SVG chart. It takes data as props,
 * fetches nothing, and imports nothing from `web/src/api.ts` — the same
 * contract `MeasurementChart` and `WeightPlanChart` each hold. Nothing wires
 * this into a screen; that is somebody else's step.
 */
export interface WeightTrajectoryChartProps {
  trajectories: WeightTrajectories;
  unit: string;
  precision: number;
  className?: string;
}

// ---------------------------------------------------------------------------
// The component
// ---------------------------------------------------------------------------
//
// The pure geometry (`linearScale`, `mapTrajectoryPoints`,
// `trajectoryTimeDomain`, `trajectoryValueDomain`) lives in
// `weightTrajectoryChartMath.ts`, imported and re-exported above — the same
// split `WeightPlanChart`/`weightPlanChartMath.ts` uses, and for the same
// reason: it can be read, and tested with `node:test`, without the test
// runner also having to load this file's CSS module import.

/**
 * Smaller margins and a squarer aspect than the taller, wider charts in this
 * app: this one is never given the room a full-width chart gets. It sits
 * beside `WeightPlanChart` in a two-column row, at roughly half the width
 * either would have alone, so the brief asks for it to stay legible there
 * rather than assume it is the widest thing on the page.
 */
const MARGIN = { top: 16, right: 12, bottom: 24, left: 40 };
const MIN_HEIGHT = 180;
const TARGET_ASPECT = 2.4;
const DEFAULT_WIDTH = 320;
const TICK_COUNT = 3;
/** Fewer, evenly-spaced labels than a full-width chart's five — a small
 *  chart crowds five date labels into illegibility long before it crowds
 *  three. */
const X_TICK_FRACTIONS = [0, 0.5, 1];
const POINT_RADIUS = 3;
const ACTIVE_POINT_RADIUS = 4;
/** A small, secondary mark — big enough to sit under the crosshair, but
 *  never as bold as `actual`'s own `ACTIVE_POINT_RADIUS`, so the reader's
 *  real weight always reads as the most prominent thing at the active
 *  week. */
const ACTIVE_REFERENCE_RADIUS = 3;
/** How close two of `required`'s permanent weekly dots may sit before they
 *  are thinned — see `weeklyMarkerIndices`'s own comment for why only this
 *  one line gets them at all. */
const REQUIRED_MARKER_MIN_SPACING_PX = 26;
const REQUIRED_MARKER_RADIUS = 2;

function heightForWidth(width: number): number {
  return Math.round(Math.max(MIN_HEIGHT, width / TARGET_ASPECT));
}

function formatValue(value: number, precision: number): string {
  return value.toFixed(Math.max(0, precision));
}

const axisTickFormatter = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });

/**
 * "wk 12 · 16 Mar" — the week number first, because that is what the owner
 * asked to navigate by, with the date kept beside it so a week number never
 * has to be decoded.
 *
 * `isoWeekNumber` comes from `chartMath`, the same function the weekly change
 * strip already numbers by. One rule for what week it is, or the two charts
 * on this page would disagree about week 53 at the turn of the year.
 */
function formatWeekTick(t: number): string {
  const week = isoWeekNumber(new Date(t).toISOString());
  const date = axisTickFormatter.format(new Date(t));
  return week === null ? date : `wk ${week} · ${date}`;
}

/**
 * One of the five lines: a path when there are at least two points, a single
 * dot when there is exactly one (a goal one week old has exactly one
 * `actual` reading, and it must still render rather than draw nothing or
 * throw on a degenerate path), and nothing at all when the line is empty —
 * the contract `projected`, `required` and `drift` all rely on when the
 * reader has not asked for them, or the goal doesn't carry what they need (a
 * baseline and deadline for `required`, a profile for `drift`). `drift` is
 * plotted the same way as every straight line here —
 * every one of its points passed straight through to `linearPath` — and that
 * is deliberate: with one point per week it is the many short straight
 * segments between real weeks, never a smoothed spline, that read as a curve
 * bending under falling expenditure.
 */
function Series({
  pixels,
  lineClassName,
  dotClassName,
  drawsOn = false,
}: {
  pixels: readonly TrajectoryPixel[];
  lineClassName: string | undefined;
  dotClassName: string | undefined;
  /**
   * Whether this line uses the draw-on reveal, which needs `pathLength={1}`
   * to work at all.
   *
   * The reveal is `stroke-dasharray: 1` with the offset animated to zero —
   * one dash covering the whole line, uncovered as it runs. Without
   * `pathLength`, that 1 is ONE USER UNIT, so the "solid" line rendered as a
   * 1px-on, 1px-off dotted hairline and stayed that way after the animation
   * finished. Measured out of the built page: the measured line came back
   * `dash=1px` where it should have been solid.
   *
   * Only for the animated line. Setting it on the others would rescale their
   * own dash patterns into a space where the whole path is 1 unit long, and
   * a `6 4` dash would come out solid.
   */
  drawsOn?: boolean;
}) {
  if (pixels.length === 0) return null;
  if (pixels.length === 1) {
    const only = pixels[0];
    if (!only) return null;
    return <circle className={dotClassName} cx={only.x} cy={only.y} r={POINT_RADIUS} aria-hidden="true" />;
  }
  return (
    <path
      className={lineClassName}
      d={linearPath(pixels)}
      {...(drawsOn ? { pathLength: 1 } : {})}
      aria-hidden="true"
    />
  );
}

/**
 * Projected weight across a goal's own span: the reader's real measured
 * weight, an optional projection at their own recent pace, an optional
 * gradual-pace reference from today (`healthyFromToday`, the one healthy
 * pace this chart draws), an optional straight run of the plan as it was
 * originally set (`required`), an optional curved run of that same gradual
 * pace held at today's intake — never revisited — (`drift`), and the target
 * and deadline as fixed markers. All five lines share ONE time axis taken
 * from `trajectories.domain` — never from the data's own extent, because
 * that domain is deliberately widened past the deadline whenever a gradual
 * pace would not make it in time, and that gap is the single most important
 * thing this chart draws. A line the domain does not stretch to fit is not a
 * bug: a line that has not reached the target by the right-hand edge has
 * visibly not made it, and neither axis nor line is adjusted to hide that.
 *
 * The crosshair is anchored to a WEEK GRID built from that same domain (see
 * `weekGridForDomain`), not to `actual` — every week between the domain's
 * ends is reachable, including a week with no measurement yet and a week
 * that has not happened yet, which is the only way to ever point at the plan
 * or the healthy pace running ahead of today.
 */
export function WeightTrajectoryChart({ trajectories, unit, precision, className }: WeightTrajectoryChartProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const focusedRef = useRef(false);

  // Keeps the viewBox's width equal to the container's real CSS width — see
  // `WeightPlanChart`'s own comment on why: 1 SVG user unit stays 1 real
  // pixel, so embedded text never shrinks along with a narrow column.
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(Math.max(1, Math.round(entry.contentRect.width)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const height = heightForWidth(width);
  const plot = useMemo(
    () => ({ x0: MARGIN.left, x1: width - MARGIN.right, y0: MARGIN.top, y1: height - MARGIN.bottom }),
    [width, height],
  );

  const [minT, maxT] = useMemo(() => trajectoryTimeDomain(trajectories.domain), [trajectories.domain]);
  const xScale = useMemo(() => linearScale(minT, maxT, plot.x0, plot.x1), [minT, maxT, plot]);

  /**
   * The crosshair's own reachable set — every week between the domain's own
   * ends, never just the weeks `actual` happens to have a reading for. See
   * `weekGridForDomain`'s own comment for why: that used to be `actual`
   * itself, and every measured week is necessarily in the past, so the plan
   * and the healthy pace running FORWARD could never be pointed at.
   */
  const weekGrid = useMemo(() => weekGridForDomain(trajectories.domain), [trajectories.domain]);
  const weekGridX = useMemo(() => weekGrid.map((t) => xScale(t)), [weekGrid, xScale]);

  const { yScale, ticks } = useMemo(() => {
    const domain = trajectoryValueDomain(trajectories, precision);
    const tickValues = buildTicks(domain, TICK_COUNT);
    const firstTick = tickValues[0] ?? domain.min;
    const lastTick = tickValues[tickValues.length - 1] ?? domain.max;
    const finalDomain = { min: Math.min(domain.min, firstTick), max: Math.max(domain.max, lastTick) };
    return { yScale: linearScale(finalDomain.min, finalDomain.max, plot.y1, plot.y0), ticks: tickValues };
  }, [trajectories, precision, plot]);

  const actualPixels = useMemo(
    () => mapTrajectoryPoints(trajectories.actual, xScale, yScale),
    [trajectories.actual, xScale, yScale],
  );
  const projectedPixels = useMemo(
    () => mapTrajectoryPoints(trajectories.projected, xScale, yScale),
    [trajectories.projected, xScale, yScale],
  );
  const healthyTodayPixels = useMemo(
    () => mapTrajectoryPoints(trajectories.healthyFromToday, xScale, yScale),
    [trajectories.healthyFromToday, xScale, yScale],
  );
  const requiredPixels = useMemo(
    () => mapTrajectoryPoints(trajectories.required, xScale, yScale),
    [trajectories.required, xScale, yScale],
  );

  /**
   * Which lines are drawn. All of them, until the reader says otherwise.
   *
   * Kept in the chart rather than lifted to the screen because it is a
   * property of LOOKING at this chart, not of the goal — nothing outside
   * needs to know, and nothing else changes when it does. The series arrays
   * are emptied rather than skipped at each draw site, so every existing
   * "an empty array draws nothing and reserves no space" rule keeps working
   * untouched, and so does the legend's own "only what was drawn".
   */
  const [hidden, setHidden] = useState<Partial<Record<TrajectorySeriesKey, boolean>>>({});
  const shown = useCallback(
    (key: TrajectorySeriesKey, points: readonly TrajectoryPixel[]) =>
      hidden[key] ? [] : points,
    [hidden],
  );
  const toggleFor = useCallback(
    (key: TrajectorySeriesKey) => ({
      checked: !hidden[key],
      onChange: (next: boolean) => setHidden((prev) => ({ ...prev, [key]: !next })),
    }),
    [hidden],
  );

  const targetY = yScale(trajectories.targetValue);
  const deadlineMs = trajectories.deadline ? Date.parse(trajectories.deadline) : null;
  const deadlineX = deadlineMs !== null && Number.isFinite(deadlineMs) ? xScale(deadlineMs) : null;
  const deadlineLabelAnchor = deadlineX !== null ? anchorForDeadlineLabel(deadlineX, plot.x1) : null;

  /**
   * Every line, named exactly the way the legend above names it — the
   * readout must never invent a second name for a line the legend already
   * labelled. Kept in the SAME order as the legend, so a reader scanning the
   * key top to bottom finds the readout listing things in the order they
   * expect. An empty series simply never produces an entry (see
   * `weeklyReadoutEntries`); it does not need to be filtered out here.
   */
  const seriesDescriptors: (NamedTrajectorySeries & {
    dotClassName: string | undefined;
    lineClassName: string | undefined;
  })[] = [
    { key: 'actual', label: 'Measured', points: trajectories.actual, dotClassName: styles.actualDot, lineClassName: styles.actualLine },
    { key: 'required', label: 'Plan', points: trajectories.required, dotClassName: styles.requiredDot, lineClassName: styles.requiredLine },
    {
      key: 'healthyFromToday',
      // The only healthy pace this chart draws now, so it needs no
      // qualifier distinguishing it from a second one.
      label: 'Healthy pace',
      points: trajectories.healthyFromToday,
      dotClassName: styles.healthyTodayDot, lineClassName: styles.healthyTodayLine,
    },
    {
      key: 'projected',
      label: 'Your recent pace',
      points: trajectories.projected,
      dotClassName: styles.projectedDot, lineClassName: styles.projectedLine,
    },
  ];
  /**
   * The same descriptors with hidden lines emptied — one filter rather than a
   * check at each draw site.
   *
   * The READOUT reads these too, so a hidden line drops out of the tooltip as
   * well as off the plot. A value quoted for a line that is not on screen is
   * just a puzzle.
   */
  const visibleSeries = seriesDescriptors.map((d) => ({
    ...d,
    points: hidden[d.key as TrajectorySeriesKey] ? [] : d.points,
  }));

  /**
   * The key, derived from the same list that draws the plot and feeds the
   * readout — never a second list with the same names in it. Only the lines
   * that have something to draw appear, and each one switches itself.
   */
  const legendItems: ChartLegendItem[] = seriesDescriptors
    .filter((d) => d.points.length > 0)
    .map((d) => ({
      label: d.label,
      className: d.lineClassName,
      toggle: toggleFor(d.key as TrajectorySeriesKey),
    }));

  const dotClassByKey = new Map(seriesDescriptors.map((d) => [d.key, d.dotClassName]));

  /**
   * `required`'s own permanent weekly markers — see `weeklyMarkerIndices`'s
   * comment for why it is the one line that gets them at all, rather than
   * every line, and why they are thinned by the width actually available.
   */
  const requiredMarkerIndices = useMemo(
    () => weeklyMarkerIndices(requiredPixels.length, Math.max(0, plot.x1 - plot.x0), REQUIRED_MARKER_MIN_SPACING_PX),
    [requiredPixels.length, plot],
  );

  const spanMs = maxT - minT;

  const sortedActual = useMemo(
    () => [...trajectories.actual].sort((a, b) => Date.parse(a.at) - Date.parse(b.at)),
    [trajectories.actual],
  );

  const last = sortedActual[sortedActual.length - 1];

  /** The grid index whose week sits closest to `atMs` — used to give the
   *  slider a sensible starting point (the most recently measured week) on
   *  first focus, without limiting where it can travel afterwards. */
  function nearestGridIndexForTime(atMs: number): number | null {
    if (weekGrid.length === 0) return null;
    let bestIndex = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < weekGrid.length; i++) {
      const distance = Math.abs(weekGrid[i]! - atMs);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }
    return bestIndex;
  }

  /** Which week of `weekGrid` sits under a pointer — the crosshair now
   *  travels the full week grid, not just the weeks `actual` has a reading
   *  for, which is the entire point of this brief: a future week, or one
   *  with no reading yet, is exactly as reachable as a measured one. */
  function nearestIndexForClientX(clientX: number, svgEl: SVGSVGElement | null): number | null {
    if (!svgEl || weekGridX.length === 0) return null;
    const rect = svgEl.getBoundingClientRect();
    if (rect.width === 0) return null;
    const viewX = ((clientX - rect.left) / rect.width) * width;
    let bestIndex = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < weekGridX.length; i++) {
      const distance = Math.abs(weekGridX[i]! - viewX);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }
    return bestIndex;
  }

  function handlePointerMove(event: PointerEvent<SVGRectElement>) {
    const index = nearestIndexForClientX(event.clientX, event.currentTarget.ownerSVGElement);
    if (index !== null) setActiveIndex(index);
  }

  function handlePointerLeave() {
    if (!focusedRef.current) setActiveIndex(null);
  }

  function handleFocus() {
    focusedRef.current = true;
    setActiveIndex((current) => current ?? (last ? nearestGridIndexForTime(Date.parse(last.at)) : weekGrid.length - 1));
  }

  function handleBlur() {
    focusedRef.current = false;
    setActiveIndex(null);
  }

  function handleKeyDown(event: KeyboardEvent<SVGRectElement>) {
    if (weekGrid.length === 0) return;
    const current = activeIndex ?? weekGrid.length - 1;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setActiveIndex(Math.min(weekGrid.length - 1, current + 1));
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setActiveIndex(Math.max(0, current - 1));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(weekGrid.length - 1);
    }
  }

  /**
   * The week the readout is anchored to — an index into `weekGrid`, so a
   * week with no `actual` reading (including one still in the future) is
   * just as reachable as a measured one. Driven by `activeIndex` alone, so
   * the mouse and the keyboard slider (which only ever move `activeIndex`)
   * produce exactly the same readout.
   */
  const activeAnchorMs = activeIndex !== null ? anchorTimeForIndex(weekGrid, activeIndex) : null;
  const activeX = activeAnchorMs !== null ? xScale(activeAnchorMs) : null;

  /**
   * "What should I weigh in this week", for every line at once: each series
   * that has a point near the active week, reporting its own nearest point —
   * see `weeklyReadoutEntries`. A series with nothing near it is simply
   * absent, never a zero or an interpolated guess — which is what lets a
   * future week report the plan and the healthy pace with no measurement at
   * all beside them.
   */
  const readoutEntries =
    activeAnchorMs !== null ? weeklyReadoutEntries(visibleSeries, activeAnchorMs, WEEKLY_READOUT_TOLERANCE_MS) : [];

  /** Where each readout row's own point actually sits, for the small marks
   *  drawn on the lines themselves at the active week (below) — computed
   *  from the entry's own point, never assumed to share the crosshair's own
   *  x, since a series with a slightly offset weekly grid has its own `at`
   *  and therefore its own `x`. */
  const readoutPixels = readoutEntries.map((entry) => ({
    entry,
    x: xScale(Date.parse(entry.point.at)),
    y: yScale(entry.point.value),
  }));

  /** `actual`'s own reading nearest the active week, if the readout found
   *  one — what tells the reading-mark loop below which dot, if any, to draw
   *  as the active one. `null` both when nothing is selected and when the
   *  active week simply has no measurement, which is exactly the case a
   *  future week now reaches. */
  const activeActualAt = readoutEntries.find((entry) => entry.key === 'actual')?.point.at ?? null;

  /** Where the tooltip anchors vertically: the first readout row's own
   *  pixel, in the same priority order the legend and readout already use
   *  (`actual` first when it has something to say, otherwise whichever
   *  forward line is nearest) — never assumed to be `actual`'s, since a
   *  future week has none. */
  const activeAnchorY = readoutPixels[0]?.y ?? null;

  function pointReadout(index: number): string {
    const atMs = weekGrid[index];
    if (atMs === undefined) return '';
    const when = axisTickFormatter.format(new Date(atMs));
    const entries = weeklyReadoutEntries(seriesDescriptors, atMs, WEEKLY_READOUT_TOLERANCE_MS);
    const lines = entries.map((e) => `${e.label} ${formatValue(e.point.value, precision)} ${unit}`).join(', ');
    return `${when} — ${lines}`;
  }

  const totalPoints =
    trajectories.actual.length +
    trajectories.projected.length +
    trajectories.healthyFromToday.length +
    trajectories.required.length;

  if (totalPoints === 0) {
    return (
      <div ref={frameRef} className={[styles.frame, className].filter(Boolean).join(' ')}>
        <div className={styles.empty}>
          <Text color="muted" size="sm">No trajectory data yet.</Text>
        </div>
      </div>
    );
  }

  const deadlineLabel = trajectories.deadline ? axisTickFormatter.format(new Date(trajectories.deadline)) : null;

  const rootClassName = [styles.frame, className].filter(Boolean).join(' ');

  const summary = last
    ? `Weight trajectory: as of ${axisTickFormatter.format(new Date(last.at))}, ${formatValue(last.value, precision)} ${unit}, against a target of ${formatValue(trajectories.targetValue, precision)} ${unit}${deadlineLabel ? ` by ${deadlineLabel}` : ''}. Use the chart's slider control to read every week, including ones still ahead.`
    : `Weight trajectory: target ${formatValue(trajectories.targetValue, precision)} ${unit}${deadlineLabel ? ` by ${deadlineLabel}` : ''}. No measured weight yet.`;

  return (
    <div ref={frameRef} className={rootClassName}>
      <svg
        className={styles.svg}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={summary}
      >
        {/* Gridlines: solid hairlines, one shade off the surface. */}
        <g aria-hidden="true">
          {ticks.map((tick) => {
            const y = yScale(tick);
            return (
              <g key={tick}>
                <line className={styles.gridline} x1={plot.x0} x2={plot.x1} y1={y} y2={y} />
                <text className={styles.tickLabel} x={plot.x0 - 6} y={y + 4} textAnchor="end">
                  {formatValue(tick, precision)}
                </text>
              </g>
            );
          })}
        </g>

        {/* x-axis labels: a sparse handful of evenly spaced points in time —
            the axis is `trajectories.domain`, so these always cover its full
            span, whatever it happens to hold. */}
        <g aria-hidden="true">
          {X_TICK_FRACTIONS.map((fraction) => {
            const t = minT + spanMs * fraction;
            const x = plot.x0 + fraction * (plot.x1 - plot.x0);
            return (
              <text
                key={fraction}
                className={styles.xTickLabel}
                x={x}
                y={height - 6}
                textAnchor={fraction === 0 ? 'start' : fraction === 1 ? 'end' : 'middle'}
              >
                {formatWeekTick(t)}
              </text>
            );
          })}
        </g>

        {/* The deadline: a dashed vertical marker, drawn behind the lines, so
            a line that crosses the target AFTER it reads instantly as late.
            Only drawn when the reader set one. */}
        {deadlineX !== null && (
          <g aria-hidden="true">
            <line className={styles.deadlineLine} x1={deadlineX} x2={deadlineX} y1={plot.y0} y2={plot.y1} />
            {/* Anchored on whichever side has room. The domain now ENDS at
                the deadline, so this line usually sits on the right-hand
                edge and a label starting there runs clean off the page —
                which is exactly what the owner saw. */}
            <text
              className={styles.deadlineLabel}
              x={deadlineLabelAnchor!.x}
              y={plot.y0 + 10}
              textAnchor={deadlineLabelAnchor!.anchor}
            >
              Deadline
            </text>
          </g>
        )}


        {/* `required`'s own permanent weekly markers — the direct answer to
            "no weekly points telling me at which weight we should be by
            then", which is what this line already IS (see its own doc
            comment above). The other five lines answer the same question
            only through the readout below, on request: six lines times
            thirty weeks is a hundred and eighty dots on a chart that never
            gets more than half the page, which would bury every line it
            marks. Thinned to the width actually available by
            `weeklyMarkerIndices`, and only drawn once there is a real path
            to mark (a single-point `required` already drew its own dot via
            `Series` above). */}
        {requiredPixels.length > 1 && (
          <g aria-hidden="true">
            {requiredMarkerIndices.map((i) => {
              const p = requiredPixels[i];
              return p ? <circle key={i} className={styles.requiredDot} cx={p.x} cy={p.y} r={REQUIRED_MARKER_RADIUS} /> : null;
            })}
          </g>
        )}

        {/* Ascending prominence, so `actual` is drawn last and sits on top.
            `healthyFromToday` is the faintest: it is a rule of thumb about
            people in general, and it goes underneath the reader's own lines
            rather than across them. `required` is drawn directly beneath
            `actual` because it IS `actual`, carried on — same accent, same
            weight, dotted — and the two have to read as one line rather than
            as a line and a neighbour. */}
        <Series pixels={shown('healthyFromToday', healthyTodayPixels)} lineClassName={styles.healthyTodayLine} dotClassName={styles.healthyTodayDot} />
        <Series pixels={shown('projected', projectedPixels)} lineClassName={styles.projectedLine} dotClassName={styles.projectedDot} />
        <Series pixels={shown('required', requiredPixels)} lineClassName={styles.requiredLine} dotClassName={styles.requiredDot} />

        {/* The target: a bold, solid horizontal line — every other line on
            this chart is read against it. */}
        <g aria-hidden="true">
          <line className={styles.targetLine} x1={plot.x0} x2={plot.x1} y1={targetY} y2={targetY} />
          <text className={styles.targetLabel} x={plot.x1 - 4} y={targetY - 6} textAnchor="end">
            Target {formatValue(trajectories.targetValue, precision)} {unit}
          </text>
        </g>

        {/* `actual`: the reader's real measured weight, the most prominent
            thing on the chart. Drawn last, on top of every reference line. */}
        <Series pixels={shown('actual', actualPixels)} lineClassName={styles.actualLine} dotClassName={styles.actualDot} drawsOn />

        {activeX !== null && (
          <line
            aria-hidden="true"
            className={styles.crosshairLine}
            x1={activeX}
            x2={activeX}
            y1={plot.y0}
            y2={plot.y1}
          />
        )}

        {actualPixels.map((p, i) => {
          const isActive = activeActualAt !== null && sortedActual[i]?.at === activeActualAt;
          return (
            <circle
              key={sortedActual[i]?.at ?? i}
              aria-hidden="true"
              className={styles.readingMark}
              cx={p.x}
              cy={p.y}
              r={isActive ? ACTIVE_POINT_RADIUS : POINT_RADIUS}
              opacity={activeAnchorMs === null || isActive ? 1 : 0.6}
            />
          );
        })}

        {/* The other lines' own points at the active week — small, secondary
            marks that tie the tooltip's rows back to the chart itself.
            Drawn only for the one week currently hovered or focused, never
            permanently, which is what keeps this from becoming the same
            hundred-and-eighty-dot clutter `required`'s own markers were kept
            sparse to avoid. `actual` is excluded: it already has its own,
            more prominent mark in the loop above. */}
        {readoutPixels
          .filter(({ entry }) => entry.key !== 'actual')
          .map(({ entry, x, y }) => (
            <circle
              key={entry.key}
              aria-hidden="true"
              className={dotClassByKey.get(entry.key)}
              cx={x}
              cy={y}
              r={ACTIVE_REFERENCE_RADIUS}
            />
          ))}

        {/* The hit layer: a generous nearest-week target and the one
            focusable control that exposes every week of the domain — the
            same slider idiom `WeightPlanChart` and `MeasurementChart` each
            use, now ranging over `weekGrid` rather than `actual`, so a week
            with no reading yet, or one still in the future, is exactly as
            reachable as a measured one. Only rendered when the domain
            actually produced a grid; a chart with no valid domain has
            nothing this control would read out. */}
        {weekGrid.length > 0 && (
          <rect
            x={plot.x0}
            y={plot.y0}
            width={Math.max(0, plot.x1 - plot.x0)}
            height={Math.max(0, plot.y1 - plot.y0)}
            className={styles.hitLayer}
            tabIndex={0}
            role="slider"
            aria-label="Weight trajectory — use the left and right arrow keys to move between weeks"
            aria-valuemin={0}
            aria-valuemax={Math.max(0, weekGrid.length - 1)}
            aria-valuenow={activeIndex ?? weekGrid.length - 1}
            aria-valuetext={pointReadout(activeIndex ?? weekGrid.length - 1)}
            onPointerMove={handlePointerMove}
            onPointerDown={handlePointerMove}
            onPointerLeave={handlePointerLeave}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />
        )}
      </svg>

      <ChartLegend items={legendItems} />


      {/* The readout: "what should I weigh in this week", for every line at
          once. One row per series that has a point near the active week
          (see `weeklyReadoutEntries`) — `actual` joins them only when the
          active week actually has a reading, which is what lets a future or
          unmeasured week show the plan and the healthy pace with nothing
          claimed as measured. Anchored on `activeX`/`activeAnchorY` — the
          active WEEK's own position, not any one series' point, since a
          future week has no `actual` pixel to anchor to at all. Vertical
          placement flips to below the point, via `tooltipVerticalAlign`,
          once a multi-row block anchored near the plot's own top margin
          would otherwise run off the frame — the horizontal flip below
          (`data-align`) already existed for the same reason on the other
          axis. */}
      {activeX !== null && activeAnchorY !== null && readoutEntries.length > 0 && (
        <div
          className={styles.tooltip}
          data-testid="trajectory-readout"
          style={{
            left: `${(activeX / width) * 100}%`,
            top: `${(activeAnchorY / height) * 100}%`,
          }}
          data-align={activeX > width * 0.75 ? 'end' : activeX < width * 0.25 ? 'start' : 'center'}
          data-vertical={tooltipVerticalAlign(activeAnchorY, plot.y0, readoutEntries.length)}
        >
          <Text as="div" size="2xs" color="muted" className={styles.tooltipTime}>
            {activeAnchorMs !== null ? formatWeekTick(activeAnchorMs) : ''}
          </Text>
          {readoutEntries.map((entry) => {
            const isMeasured = entry.key === 'actual';
            return (
              <div key={entry.key} className={styles.tooltipRow}>
                <svg className={styles.tooltipSwatch} viewBox="0 0 8 8" aria-hidden="true">
                  <circle className={dotClassByKey.get(entry.key)} cx="4" cy="4" r="3" />
                </svg>
                <Text
                  as="span"
                  family="figure"
                  size={isMeasured ? 'md' : '2xs'}
                  color={isMeasured ? 'text' : 'muted'}
                  className={isMeasured ? styles.tooltipValue : undefined}
                >
                  {entry.label} — {formatValue(entry.point.value, precision)} {unit}
                </Text>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
